import { useEffect, useState, useMemo, useContext } from 'react';

import { SnackbarContext, SnackbarVariant } from '@eas/common-web';

import { Backend } from '../../../../api/endpoints';
import { fetchLicenceRights, fetchCardRights } from '../new-card-api';
import { useIntl } from 'react-intl';

export function useCardRights() {
  const [loading, setLoading] = useState(true);
  const [licences, setLicences] = useState<Backend.LicenceRight[]>([]);
  const [cards, setCards] = useState<Backend.CardRight[]>([]);
  const { showSnackbar } = useContext(SnackbarContext);
  const intl = useIntl();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const { items: licenceItems } = await fetchLicenceRights();
        const { items: cardItems } = await fetchCardRights();
        // Remove EUSP card on FE
        const filteredCardItems = cardItems?.filter(
          (card) => card.cardType !== 'EUSP'
        );

        setLicences(licenceItems ?? []);
        setCards(filteredCardItems ?? []);
      } catch (err) {
        showSnackbar(
          intl.formatMessage({
            id: 'VPUK__NEW_CARD__CARD_RIGHTS__LOADING_FAILED',
          }),
          SnackbarVariant.ERROR
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [intl, showSnackbar]);

  const possibleLicences = useMemo(
    () =>
      licences
        .filter(
          ({ possibleToUse, cardAlreadyExists = false, price }) =>
            possibleToUse && !cardAlreadyExists && price !== null
        )
        .map((licence, index) => ({
          ...licence,
          id: `${licence.cardType}-${licence.licenceType}-${index}`,
        })),
    [licences]
  );

  const possibleCards = useMemo(
    () =>
      cards
        .filter(({ possibleToUse }) => possibleToUse)
        .map((card, index) => ({
          ...card,
          id: `${card.cardType}-None-${index}`,
        })),
    [cards]
  );

  const cardsAndLicences = useMemo(
    () => [...possibleLicences, ...possibleCards],
    [possibleCards, possibleLicences]
  );

  return { licences, possibleLicences, cardsAndLicences, loading };
}
